/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AirportCodeType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AirportProps {
  id: string;
  name: string;
  abbreviation: string;
  code: string;
  airportType: string;
  airportCodeType: AirportCodeType;
  sourceSystemId: number | null;
  preferredFboId: string | null;
  locationId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class AirportRecord extends Record<AirportProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  code: null as any,
  airportType: null as any,
  airportCodeType: null as any,
  sourceSystemId: null as any,
  preferredFboId: null as any,
  locationId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    code: string;
    airportType: string;
    airportCodeType: AirportCodeType;
    sourceSystemId: number | null;
    preferredFboId: string | null;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): AirportRecord {
    if (json == null) {
      throw new Error('AirportRecord.fromJS: parameter cannot be null');
    }
    return new AirportRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'AirportJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'AirportJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'AirportJson.abbreviation'),
      code: getFromOrThrow(json, (j) => j.code, 'AirportJson.code'),
      airportType: getFromOrThrow(json, (j) => j.airportType, 'AirportJson.airportType'),
      airportCodeType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'airportCodeType', AirportCodeType.ICAO),
        'AirportJson.airportCodeType',
      ),
      sourceSystemId: json.sourceSystemId,
      preferredFboId: json.preferredFboId,
      locationId: json.locationId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    code: string;
    airportType: string;
    airportCodeType: AirportCodeType;
    sourceSystemId: number | null;
    preferredFboId: string | null;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [AirportRecord, Error[]] {
    if (json == null) {
      throw new Error('AirportRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AirportRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'AirportJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'AirportJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'AirportJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      code: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.code, 'AirportJson.code'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      airportType: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.airportType, 'AirportJson.airportType'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      airportCodeType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'airportCodeType', AirportCodeType.ICAO),
            'AirportJson.airportCodeType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sourceSystemId: tryOrDefault(
        () => json.sourceSystemId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      preferredFboId: tryOrDefault(
        () => json.preferredFboId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: AirportProps) {
    super(props);
    if (props == null) {
      throw new Error('AirportRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AirportViewModelBase implements AirportProps {
  protected readonly _props: AirportProps;
  private _json?: AirportProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get code(): string {
    return this._props.code;
  }
  get airportType(): string {
    return this._props.airportType;
  }
  get airportCodeType(): AirportCodeType {
    return this._props.airportCodeType;
  }
  get sourceSystemId(): number | null {
    return this._props.sourceSystemId;
  }
  get preferredFboId(): string | null {
    return this._props.preferredFboId;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isFAA() {
    return this._props.airportCodeType === AirportCodeType.FAA;
  }
  public get isIATA() {
    return this._props.airportCodeType === AirportCodeType.IATA;
  }
  public get isICAO() {
    return this._props.airportCodeType === AirportCodeType.ICAO;
  }

  public isAnyAirportCodeType(...airportCodeType: AirportCodeType[]) {
    return airportCodeType.includes(this._props.airportCodeType);
  }

  constructor(props: AirportProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        code: this._props.code,
        airportType: this._props.airportType,
        airportCodeType: this._props.airportCodeType,
        sourceSystemId: this._props.sourceSystemId,
        preferredFboId: this._props.preferredFboId,
        locationId: this._props.locationId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllAirportEntities = (state: AppState) => state.entityStores.airport.entities;

export const selectAirportById = (id: string) => (state: AppState) =>
  state.entityStores.airport.entities.get(id);

export const selectAirportMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.airport.meta.get(id);

export const selectAirportAndMetadataById = (id: string) =>
  createSelector(selectAirportById(id), selectAirportMetadataById(id), (airport, meta) => ({
    airport,
    meta,
  }));

export function selectAirportEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllAirportEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, AirportRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectAirportEntitiesByName(name: string) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectAirportEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectAirportEntitiesByCode(code: string) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.code === code).valueSeq(),
  );
}

export function selectAirportEntitiesByAirportType(airportType: string) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.airportType === airportType).valueSeq(),
  );
}

export function selectAirportEntitiesByAirportCodeType(airportCodeType: AirportCodeType) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.airportCodeType === airportCodeType).valueSeq(),
  );
}

export function selectAirportEntitiesBySourceSystemId(sourceSystemId: number) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.sourceSystemId === sourceSystemId).valueSeq(),
  );
}

export function selectAirportEntitiesByPreferredFboId(preferredFboId: string) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.preferredFboId === preferredFboId).valueSeq(),
  );
}

export function selectAirportEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllAirportEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}
//#endregion Selectors
